import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { KitchenProvider, useKitchen } from "./contexts/KitchenContext";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import UserManagement from "./pages/UserManagement";
import Logistics from "./pages/Logistics";
import SecurityGate from "./pages/SecurityGate";
import VesselManagement from "./pages/VesselManagement";
import StoresManagement from "./pages/StoresManagement";
import Purchase from "./pages/Purchase";
import TaskManager from "./pages/TaskManager";
import AdminSettings from "./pages/AdminSettings";
import Reports from "./pages/Reports";
import MenuRecipes from "./pages/MenuRecipes";
import Attendance from "./pages/Attendance";
import KioskPage from "./pages/KioskPage";
import HRModule from "./pages/HRModule";
import AssetManagement from "./pages/AssetManagement";
import OperationsExtra from "./pages/OperationsExtra";
import AIAnalytics from "./pages/AIAnalytics";
import DailyEntry from "./pages/DailyEntry";
import VendorManagement from "./pages/VendorManagement";
import SchoolIndent from "./pages/SchoolIndent";
import Accounts from "./pages/Accounts";
import HolidayCalendar from "./pages/HolidayCalendar";
import RecipeCostCalculator from "./pages/RecipeCostCalculator";
import QualityInspection from "./pages/QualityInspection";
import DeliveryTracking from "./pages/DeliveryTracking";
import VehicleTracking from "./pages/VehicleTracking";
import DeliveryKiosk from "./pages/DeliveryKiosk";
import DriverTracking from "./pages/DriverTracking";
import InspectorVisits from "./pages/InspectorVisits";
import DeletionLog from "./pages/DeletionLog";
import GateLog from "./pages/GateLog";
import Reconciliation from "./pages/Reconciliation";
import ReviewRequired from "./pages/ReviewRequired";
import ReportFormats from "./pages/ReportFormats";
import ProductionPlanning from "./pages/ProductionPlanning";
import SchoolCompliance from "./pages/SchoolCompliance";
import RoutePlanner from "./pages/RoutePlanner";
import UtilityCosts from "./pages/UtilityCosts";
import NoAccess from "./pages/NoAccess";
import CoordinatorDashboard from "./pages/CoordinatorDashboard";
import CallCentre from "./pages/CallCentre";
import ForcePasswordReset from "./pages/ForcePasswordReset";
import "./App.css";
import { AppErrorBoundary, ErrorBoundary } from "./components/ErrorBoundary";

/**
 * ModuleGuard — wraps a route and shows NoAccess if the user lacks the required permission.
 * Admins bypass all checks. If no permission is required, all authenticated users can access.
 */
function ModuleGuard({ children, permission, moduleName }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#E8956D] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#5C5147] font-medium">Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Admins bypass all module checks
  if (user.is_admin) return children;
  // No permission required → open to all authenticated users
  if (!permission) return children;
  // Check specific permission
  if (!user.permissions?.[permission]) {
    return <NoAccess module={moduleName || permission} />;
  }
  return children;
}

function ForceResetPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = location.state?.token;
  if (!token) return <Navigate to="/login" replace />;
  return <ForcePasswordReset resetToken={token} />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const kitchenCtx = useKitchen();
  const location = useLocation();

  // Load kitchens from user object whenever user is set (most reliable source)
  useEffect(() => {
    if (user?.kitchens && kitchenCtx?.loadKitchens) {
      kitchenCtx.loadKitchens(user.kitchens);
    }
  }, [user?.kitchens, user?.id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
      <div className="w-12 h-12 border-4 border-[#E8956D] border-t-transparent rounded-full animate-spin mx-auto"></div>
    </div>
  );

  // /delivery-kiosk and /driver run fullscreen, no Layout, require auth
  if (location.pathname === "/delivery-kiosk") {
    if (!user) return <Navigate to="/login" replace />;
    return <Routes><Route path="/delivery-kiosk" element={<DeliveryKiosk />} /></Routes>;
  }
  if (location.pathname === "/driver") {
    if (!user) return <Navigate to="/login" replace />;
    return <Routes><Route path="/driver" element={<DriverTracking />} /></Routes>;
  }

  // /kiosk is a shared-device page — no auth required (spec: standalone access model)
  if (location.pathname === "/kiosk") {
    return <Routes><Route path="/kiosk" element={<KioskPage />} /></Routes>;
  }

  if (!user) return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/force-reset" element={<ForceResetPage />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );

  // Route Coordinator role gets a dedicated minimal dashboard — no admin sidebar
  if (user.role_name === "Route Coordinator" && !user.is_admin) {
    return (
      <Routes>
        <Route path="*" element={<CoordinatorDashboard />} />
      </Routes>
    );
  }
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<ErrorBoundary pageName="Dashboard"><Dashboard /></ErrorBoundary>} />
        <Route path="/dashboard" element={<ErrorBoundary pageName="Dashboard"><Dashboard /></ErrorBoundary>} />

        {/* Settings & Admin */}
        <Route path="/users" element={<ModuleGuard permission="users_view" moduleName="user_management"><ErrorBoundary pageName="User Management"><UserManagement /></ErrorBoundary></ModuleGuard>} />
        <Route path="/admin" element={<ModuleGuard permission="admin_view" moduleName="admin_settings"><ErrorBoundary pageName="Admin Settings"><AdminSettings /></ErrorBoundary></ModuleGuard>} />
        <Route path="/report-formats" element={<ModuleGuard permission="users_view" moduleName="report_formats"><ErrorBoundary pageName="Report Formats"><ReportFormats /></ErrorBoundary></ModuleGuard>} />
        <Route path="/review" element={<ModuleGuard permission="users_view" moduleName="review"><ErrorBoundary pageName="Review Required"><ReviewRequired /></ErrorBoundary></ModuleGuard>} />
        <Route path="/deletion-log" element={<ModuleGuard permission="reports_view" moduleName="deletion_log"><ErrorBoundary pageName="Deletion Log"><DeletionLog /></ErrorBoundary></ModuleGuard>} />

        {/* Security & Gate */}
        <Route path="/security" element={<ModuleGuard permission="security_view" moduleName="security_gate"><ErrorBoundary pageName="Security / Gate"><SecurityGate /></ErrorBoundary></ModuleGuard>} />
        <Route path="/gate-log" element={<ModuleGuard permission="security_view" moduleName="gate_log"><ErrorBoundary pageName="Gate Log"><GateLog /></ErrorBoundary></ModuleGuard>} />

        {/* Daily Operations */}
        <Route path="/vessels" element={<ModuleGuard permission="vessels_view" moduleName="vessels"><ErrorBoundary pageName="Vessel Management"><VesselManagement /></ErrorBoundary></ModuleGuard>} />
        <Route path="/daily-entry" element={<ErrorBoundary pageName="Daily Entry Hub"><DailyEntry /></ErrorBoundary>} />
        <Route path="/school-indent" element={<ErrorBoundary pageName="School Indent"><SchoolIndent /></ErrorBoundary>} />
        <Route path="/reconciliation" element={<ErrorBoundary pageName="MDM Reconciliation"><Reconciliation /></ErrorBoundary>} />

        {/* Kitchen & Menu */}
        <Route path="/menu" element={<ModuleGuard permission="menu_view" moduleName="menu"><ErrorBoundary pageName="Kitchen & Menu"><MenuRecipes /></ErrorBoundary></ModuleGuard>} />
        <Route path="/production" element={<ModuleGuard permission="menu_view" moduleName="production"><ErrorBoundary pageName="Production Planning"><ProductionPlanning /></ErrorBoundary></ModuleGuard>} />
        <Route path="/recipe-cost" element={<ModuleGuard permission="menu_view" moduleName="recipe_cost"><ErrorBoundary pageName="Recipe Cost"><RecipeCostCalculator /></ErrorBoundary></ModuleGuard>} />

        {/* Stores */}
        <Route path="/stores" element={<ModuleGuard permission="stores_view" moduleName="stores"><ErrorBoundary pageName="Stores Management"><StoresManagement /></ErrorBoundary></ModuleGuard>} />

        {/* Purchase & Vendors */}
        <Route path="/purchase" element={<ModuleGuard permission="purchase_view" moduleName="purchase"><ErrorBoundary pageName="Purchase"><Purchase /></ErrorBoundary></ModuleGuard>} />
        <Route path="/vendors" element={<ModuleGuard permission="purchase_view" moduleName="vendors"><ErrorBoundary pageName="Vendor Management"><VendorManagement /></ErrorBoundary></ModuleGuard>} />

        {/* Logistics */}
        <Route path="/logistics" element={<ModuleGuard permission="logistics_view" moduleName="logistics"><ErrorBoundary pageName="Logistics"><Logistics /></ErrorBoundary></ModuleGuard>} />
        <Route path="/route-planner" element={<ModuleGuard permission="route_planner" moduleName="route_planner"><ErrorBoundary pageName="Route Planner"><RoutePlanner /></ErrorBoundary></ModuleGuard>} />
        <Route path="/delivery" element={<ModuleGuard permission="logistics_view" moduleName="delivery"><ErrorBoundary pageName="Delivery Tracking"><DeliveryTracking /></ErrorBoundary></ModuleGuard>} />
        <Route path="/vehicle-tracking" element={<ModuleGuard permission="logistics_view" moduleName="vehicle_tracking"><ErrorBoundary pageName="Vehicle Tracking"><VehicleTracking /></ErrorBoundary></ModuleGuard>} />

        {/* Finance */}
        <Route path="/accounts" element={<ModuleGuard permission="admin_view" moduleName="accounts"><ErrorBoundary pageName="Accounts & Billing"><Accounts /></ErrorBoundary></ModuleGuard>} />
        <Route path="/utility-costs" element={<ModuleGuard permission="admin_view" moduleName="utility_costs"><ErrorBoundary pageName="Utility Costs"><UtilityCosts /></ErrorBoundary></ModuleGuard>} />

        {/* People & HR */}
        <Route path="/hr" element={<ModuleGuard permission="hr_view" moduleName="hr"><ErrorBoundary pageName="People & HR"><HRModule /></ErrorBoundary></ModuleGuard>} />
        <Route path="/attendance" element={<ModuleGuard permission="hr_view" moduleName="attendance"><ErrorBoundary pageName="Attendance"><Attendance /></ErrorBoundary></ModuleGuard>} />
        <Route path="/holidays" element={<ErrorBoundary pageName="Holiday Calendar"><HolidayCalendar /></ErrorBoundary>} />

        {/* Assets & Quality */}
        <Route path="/assets" element={<ErrorBoundary pageName="Asset Management"><AssetManagement /></ErrorBoundary>} />
        <Route path="/quality" element={<ErrorBoundary pageName="Quality Inspection"><QualityInspection /></ErrorBoundary>} />
        <Route path="/inspector-visits" element={<ErrorBoundary pageName="Inspector Visits"><InspectorVisits /></ErrorBoundary>} />
        <Route path="/compliance" element={<ErrorBoundary pageName="School Compliance"><SchoolCompliance /></ErrorBoundary>} />
        <Route path="/operations" element={<ErrorBoundary pageName="Operations"><OperationsExtra /></ErrorBoundary>} />

        {/* Tasks */}
        <Route path="/tasks" element={<ErrorBoundary pageName="Task Manager"><TaskManager /></ErrorBoundary>} />

        {/* Call Centre */}
        <Route path="/call-centre" element={<ModuleGuard permission="call_centre" moduleName="call_centre"><ErrorBoundary pageName="Call Centre"><CallCentre /></ErrorBoundary></ModuleGuard>} />

        {/* Reports & AI */}
        <Route path="/reports" element={<ModuleGuard permission="reports_view" moduleName="reports"><ErrorBoundary pageName="Reports"><Reports /></ErrorBoundary></ModuleGuard>} />
        <Route path="/ai" element={<ModuleGuard permission="reports_view" moduleName="ai_analytics"><ErrorBoundary pageName="Reports & AI"><AIAnalytics /></ErrorBoundary></ModuleGuard>} />

        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <KitchenProvider>
            <AppRoutes />
          </KitchenProvider>
        </AuthProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

export default App;
